import { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Mutation, SessionCrudResult } from "@models/ISchema";
import { useSettingsStore } from "@context/useSettingsStore";
import { isGroupDropDateEnabled, unassignedInstructorsInput } from "@utils/unassignedInstructors";
import {
  buildAssignationChangesets,
  buildUnassignedInstructorIds,
} from "../utils/assignationChangesets";
import { IParams } from "@models/IParams";
import { AppContext } from "../context/EditSessionsContext";
import { sessionsCrud } from "../graphql/sessionsCrud.mutation";

interface MutationProps {
  dryRun: boolean;
  skipValidations: boolean;
}

interface SessionsCrudState {
  data: SessionCrudResult | null;
  error: boolean;
}

type SubmitSessionsCrud = () => Promise<SessionCrudResult | null>;

const useSessionsCrudMutation = (props: MutationProps): [SessionsCrudState, SubmitSessionsCrud] => {
  const { dryRun, skipValidations } = props;
  const { scenario, origin }: IParams = useParams();
  const { state } = useContext(AppContext);
  const isDropDateEnabled = useSettingsStore(settings =>
    isGroupDropDateEnabled(settings.instructorLinkTags),
  );

  const [sessionsCrudMutation] = useMutation<Mutation>(sessionsCrud, {});
  const [result, setResult] = useState<SessionCrudResult | null>(null);
  const [error, setError] = useState(false);

  const setVariables = () => ({
    dryRun,
    skipValidations,
    linkId: state?.link?.info?.id,
    changesets: buildAssignationChangesets(state?.form),
    ...unassignedInstructorsInput(
      buildUnassignedInstructorIds(state?.form, state?.link?.sections ?? []),
      isDropDateEnabled,
    ),
  });

  const submitSessionsCrud = async () => {
    setError(false);
    try {
      const variables = {
        scenarioId: scenario,
        originId: origin,
        input: setVariables(),
      };
      const { data } = await sessionsCrudMutation({ variables });
      const response = data?.cube?.sessionsCrud ?? null;
      setResult(response);
      return response;
    } catch (error) {
      setError(true);
      console.error("[SessionsCrud]: ", error);
      return null;
    }
  };

  return [{ data: result, error }, submitSessionsCrud];
};

export default useSessionsCrudMutation;
